"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

export function NavbarShell({ children }: { children: ReactNode }) {
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 10);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /**
   * Publish the actual rendered navbar height to `--layout-navbar-h` on
   * <html>. Sticky descendants (NicheDetailV2 + PlanPresentationV2 back-bars,
   * WaterfallScrollPin pin-top) use this var to sit flush below the navbar.
   * The value in globals.css is a 64px SSR fallback; real height varies by
   * viewport (logo size / padding scale on lg+ via `md:px-8`) — hardcoding
   * would leave a visible gap on devices where the rendered navbar is taller
   * (user reported a gap on a 393pt iPhone where the header was 70+ px).
   * ResizeObserver picks up viewport / font-loading changes.
   */
  useEffect(() => {
    const header = headerRef.current;
    if (!header || typeof document === "undefined") return;
    const update = () => {
      const h = header.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty(
          "--layout-navbar-h",
          `${h}px`,
        );
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(header);
    return () => ro.disconnect();
  }, []);

  return (
    <header
      ref={headerRef}
      className={`sticky top-0 z-50 flex items-center justify-between px-4 py-3 backdrop-blur-xl transition-all duration-300 md:px-8 ${
        scrolled
          ? "border-b border-border bg-background/90 shadow-sm"
          : "border-b border-transparent bg-background/60"
      }`}
    >
      {children}
    </header>
  );
}
